import React, { useEffect, useState, useRef } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, X, Barcode as BarcodeIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import JsBarcode from "jsbarcode";
import { useAuth } from "@/contexts/AuthContext";

function BarcodeSvg({ value, height = 50 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try {
        JsBarcode(ref.current, value, { format: "CODE128", displayValue: true, height, fontSize: 12, margin: 4 });
      } catch {}
    }
  }, [value, height]);
  return <svg ref={ref} />;
}

const empty = { name: "", sku: "", barcode: "", category_id: "", price: 0, cost: 0, stock: 0, min_stock: 5, unit: "pcs", outlet_id: "", image_url: "", active: true };

export default function Products() {
  const { hasFeature } = useAuth();
  const canPrintBarcode = hasFeature("barcode_print");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [printList, setPrintList] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const [p, c, o] = await Promise.all([api.get("/products"), api.get("/categories"), api.get("/outlets")]);
    setProducts(p.data || []); setCategories(c.data || []); setOutlets(o.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = {
        ...form,
        price: Number(form.price),
        cost: Number(form.cost),
        stock: Number(form.stock),
        min_stock: Math.max(0, Number(form.min_stock || 0)),
        outlet_id: form.outlet_id || null,
      };
      if (editId) await api.patch(`/products/${editId}`, body);
      else await api.post("/products", body);
      toast.success("Produk tersimpan"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus produk?")) return;
    await api.delete(`/products/${id}`); toast.success("Terhapus"); load();
  };

  const addCat = async () => {
    const name = prompt("Nama kategori baru?"); if (!name) return;
    await api.post("/categories", { name }); load();
  };

  const printBarcodes = () => {
    if (!canPrintBarcode) { toast.error("Fitur cetak barcode tidak tersedia pada paket saat ini"); return; }
    if (printList.length === 0) { toast.error("Pilih produk dulu"); return; }
    const win = window.open("", "_blank", "width=800,height=600");
    win.document.write(`<html><head><title>Cetak Barcode</title><style>body{font-family:sans-serif;padding:16px;}.label{display:inline-block;border:1px solid #ddd;padding:8px;margin:4px;text-align:center;width:200px;}.name{font-weight:bold;font-size:11px;margin-bottom:4px;}.price{font-size:13px;font-weight:bold;margin-top:4px;}</style></head><body>`);
    printList.forEach((id) => {
      const p = products.find((x) => x.id === id); if (!p) return;
      win.document.write(`<div class="label"><div class="name">${p.name}</div><svg id="bc-${p.id}"></svg><div class="price">${formatIDR(p.price)}</div></div>`);
    });
    win.document.write(`<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script><script>window.onload=function(){`);
    printList.forEach((id) => {
      const p = products.find((x) => x.id === id); if (!p) return;
      win.document.write(`try{JsBarcode("#bc-${p.id}","${p.barcode || p.sku || p.id}",{format:"CODE128",height:50,fontSize:12});}catch(e){}`);
    });
    win.document.write(`setTimeout(function(){window.print();},300);}<\/script></body></html>`);
    win.document.close();
  };

  const filtered = products.filter((p) => !q || p.name?.toLowerCase().includes(q.toLowerCase()) || p.sku?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">KATALOG</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Produk & Barcode</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={addCat} data-testid="prod-add-category-btn" className="px-4 py-2.5 text-sm border border-border hover:bg-zinc-100">+ Kategori</button>
          {canPrintBarcode && <button onClick={printBarcodes} data-testid="prod-print-barcode-btn" className="px-4 py-2.5 text-sm border border-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1"><Printer className="h-3.5 w-3.5"/>Cetak {printList.length>0?`(${printList.length})`:""}</button>}
          <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="prod-add-btn" className="px-4 py-2.5 text-sm bg-[#002FA7] text-white font-semibold inline-flex items-center gap-1"><Plus className="h-4 w-4"/>Produk Baru</button>
        </div>
      </div>

      <div className="mb-4">
        <input data-testid="prod-search-input" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cari produk…" className="w-full md:w-80 border border-border px-4 py-2.5 text-sm" />
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border">
            <tr>
              {canPrintBarcode && <th className="text-left p-3 w-8"></th>}
              <th className="text-left p-3 overline">Nama</th>
              <th className="text-left p-3 overline">SKU / Barcode</th>
              <th className="text-left p-3 overline">Kategori</th>
              <th className="text-right p-3 overline">Harga</th>
              <th className="text-right p-3 overline">HPP</th>
              <th className="text-right p-3 overline">Stok / Minimum</th>
              <th className="text-right p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 && <tr><td colSpan={canPrintBarcode ? 8 : 7} className="p-8 text-center text-zinc-500">Belum ada produk</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-zinc-50">
                {canPrintBarcode && <td className="p-3"><input type="checkbox" checked={printList.includes(p.id)} onChange={(e)=>setPrintList(e.target.checked?[...printList,p.id]:printList.filter(x=>x!==p.id))} data-testid={`prod-checkbox-${p.id}`}/></td>}
                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3 font-mono text-xs">{p.barcode || p.sku}</td>
                <td className="p-3 text-zinc-500">{categories.find(c=>c.id===p.category_id)?.name || "-"}</td>
                <td className="p-3 text-right tabular-nums">{formatIDR(p.price)}</td>
                <td className="p-3 text-right tabular-nums text-zinc-500">{formatIDR(p.cost)}</td>
                <td className="p-3 text-right tabular-nums">
                  <div className={(Number(p.stock || 0) <= Number(p.min_stock ?? 5)) ? "font-semibold text-rose-700" : "font-semibold"}>
                    {p.stock ?? 0} {p.unit || "pcs"}
                  </div>
                  <div className="text-[10px] text-zinc-500">min. {p.min_stock ?? 5}</div>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => { setForm({...empty, ...p}); setEditId(p.id); setOpen(true); }} className="p-1.5 hover:bg-zinc-200" data-testid={`prod-edit-${p.id}`}><Pencil className="h-3.5 w-3.5"/></button>
                  <button onClick={() => del(p.id)} className="p-1.5 hover:bg-rose-100 text-rose-600" data-testid={`prod-del-${p.id}`}><Trash2 className="h-3.5 w-3.5"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="prod-dialog">
          <DialogHeader><DialogTitle>{editId?"Edit":"Tambah"} Produk</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="overline">Nama</label><input data-testid="prod-form-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
            <div><label className="overline">SKU</label><input data-testid="prod-form-sku" value={form.sku||""} onChange={(e)=>setForm({...form,sku:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
            <div><label className="overline">Barcode</label><input data-testid="prod-form-barcode" value={form.barcode||""} onChange={(e)=>setForm({...form,barcode:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
            <div><label className="overline">Kategori</label>
              <select data-testid="prod-form-category" value={form.category_id||""} onChange={(e)=>setForm({...form,category_id:e.target.value})} className="w-full border border-border px-3 py-2 mt-1">
                <option value="">-</option>{categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="overline">Outlet (opsional)</label>
              <select data-testid="prod-form-outlet" value={form.outlet_id||""} onChange={(e)=>setForm({...form,outlet_id:e.target.value})} className="w-full border border-border px-3 py-2 mt-1">
                <option value="">Semua Outlet</option>{outlets.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div><label className="overline">Harga Jual</label><input data-testid="prod-form-price" type="number" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})} className="w-full border border-border px-3 py-2 mt-1 font-mono-num"/></div>
            <div><label className="overline">HPP / Modal</label><input data-testid="prod-form-cost" type="number" value={form.cost} onChange={(e)=>setForm({...form,cost:e.target.value})} className="w-full border border-border px-3 py-2 mt-1 font-mono-num"/></div>
            <div><label className="overline">Stok</label><input data-testid="prod-form-stock" type="number" min="0" value={form.stock} onChange={(e)=>setForm({...form,stock:e.target.value})} className="w-full border border-border px-3 py-2 mt-1 font-mono-num"/></div>
            <div><label className="overline">Batas Stok Rendah</label><input data-testid="prod-form-min-stock" type="number" min="0" value={form.min_stock ?? 5} onChange={(e)=>setForm({...form,min_stock:e.target.value})} className="w-full border border-border px-3 py-2 mt-1 font-mono-num"/></div>
            <div><label className="overline">Satuan</label><input data-testid="prod-form-unit" value={form.unit||"pcs"} onChange={(e)=>setForm({...form,unit:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
            <div className="col-span-2"><label className="overline">URL Gambar (opsional)</label><input data-testid="prod-form-image" value={form.image_url||""} onChange={(e)=>setForm({...form,image_url:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
            {form.barcode && <div className="col-span-2 flex items-center gap-3 bg-zinc-50 p-3 border border-border"><BarcodeIcon className="h-4 w-4"/><BarcodeSvg value={form.barcode}/></div>}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={()=>setOpen(false)} className="flex-1 py-3 border border-border" data-testid="prod-cancel-btn">Batal</button>
            <button onClick={save} className="flex-1 py-3 bg-[#002FA7] text-white font-semibold" data-testid="prod-save-btn">Simpan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
